import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";

/**
 * Questions the agents raised.
 *
 * Answering here is NOT replying to the customer (ADR-H4): what is written
 * arms the agent, which then speaks in its own voice. So an answer can be
 * blunt, internal and specific without any of it reaching anyone outside.
 */
export interface HumanRequest {
	readonly id: string;
	/**
	 * `session` since migration 0037 — a question raised from a live chat session.
	 * `onboarding` since 0102 — a gap the first-day research could not confirm;
	 * answering feeds curation (draft → review → company brief), nothing resumes.
	 */
	readonly subjectKind: "conversation" | "mission_run" | "session" | "onboarding";
	readonly subjectId: string;
	readonly askedByAgent: string;
	readonly question: string;
	readonly context: string | null;
	readonly status: "open" | "answered" | "dismissed";
	readonly answer: string | null;
	readonly answerFiles: readonly string[];
	readonly askedAt: string;
	readonly answeredAt: string | null;
	/** Quem respondeu — o id do colega; o nome a tela acha na equipe. */
	readonly answeredBy?: string | null;
}

/** What a question can be about, when the caller wants only one subject's. */
export interface HelpSubject {
	readonly kind: HumanRequest["subjectKind"];
	readonly id: string;
}

export const helpKeys = {
	list: (status: string, subject?: HelpSubject | null) =>
		subject ? (["help", status, subject.kind, subject.id] as const) : (["help", status] as const),
};

/**
 * The questions waiting on a person, optionally only the ones about one subject.
 *
 * The status is a `MaybeRefOrGetter` and both the key and the URL read it through
 * `toValue`. It used to be a plain string: `HelpPage` passed `status.value` at setup, so
 * the query key was fixed at whatever the filter said when the screen mounted and clicking
 * "respondidas" changed a highlighted button and nothing else.
 *
 * The subject narrows in SQL and not over the page that came back: the route answers with
 * the fifty most recent, and the open question about the conversation somebody is reading
 * is rarely among them. Both halves travel or neither — the server ignores a lone kind on
 * purpose, so sending one would quietly list every conversation's questions.
 *
 * A caller that only wants one subject's questions passes `enabled` too, so that with no
 * subject chosen yet it asks for nothing instead of polling the whole workspace.
 */
export function useHelpRequests(
	status: MaybeRefOrGetter<"open" | "answered" | "all"> = "open",
	subject: MaybeRefOrGetter<HelpSubject | null | undefined> = null,
	enabled: MaybeRefOrGetter<boolean> = true,
	/**
	 * A cadência de quem observa — e o padrão é NENHUMA (27/08, dono: nenhuma
	 * rota em polling). Sem assunto, esta lista atualiza por invalidação (o
	 * stream de tenant invalida `["help"]` a cada turno fechado) e por foco.
	 * COM assunto ela é alguém esperando UMA resposta acontecer (onboarding) —
	 * o único caso que mantém relógio, e ele morre com a tela.
	 */
	interval: number | false | null = null,
) {
	return useQuery({
		enabled: computed(() => toValue(enabled)),
		queryKey: computed(() => helpKeys.list(toValue(status), toValue(subject))),
		queryFn: async (): Promise<{ items: HumanRequest[]; open: number }> => {
			const at = toValue(subject);
			const query = new URLSearchParams({ status: toValue(status) });
			if (at) {
				query.set("subjectKind", at.kind);
				query.set("subjectId", at.id);
			}
			return request(`/v3/help?${query.toString()}`);
		},
		/**
		 * Someone is waiting on the other side of these — and HOW long they are
		 * willing to wait depends on where they are standing.
		 *
		 * Scoped to a subject means a screen is watching ONE thing happen: the
		 * onboarding run, paused mid-research on a question the person is looking
		 * at. Twenty seconds there is twenty seconds of a stopped mission looking
		 * like a broken product. Unscoped is the counter on the home, and that one
		 * can take its time.
		 */
		refetchInterval: () => (interval !== null ? interval : toValue(subject) ? 5_000 : false),
		/**
		 * ── POR QUE AS DUAS EXCEÇÕES AO PADRÃO GLOBAL ─────────────────────────
		 *
		 * `refetchInterval` só dispara com a janela em foco, e o cliente global
		 * desliga `refetchOnWindowFocus`. As duas juntas fazem esta lista PARAR
		 * numa aba em segundo plano e não se recuperar ao voltar — que é
		 * exactamente o que uma missão de dez minutos garante que acontece: a
		 * pessoa vai fazer outra coisa enquanto espera. A mesma correcção que
		 * `useOnboardingState` e `useMission` já carregam, pelo mesmo motivo.
		 */
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: true,
	});
}

/**
 * TUDO O QUE UMA RESPOSTA MOVE, invalidado junto.
 *
 * Era só `["help"]`, e por isso responder era metade do trabalho: a resposta
 * entrava e a tela ao lado continuava a dizer o de antes. As lacunas do primeiro
 * dia são as MESMAS linhas (`human_request`) que `/v3/memory/gaps` lista, o passo
 * "perguntas" da home conta-as, e o portão do site (`siteMissing.open_gaps`) só
 * abre quando a última é respondida. Três telas que mudavam de verdade e só
 * contavam depois de um F5 — que é precisamente o defeito relatado.
 */
function invalidateEverythingAnAnswerMoves(client: ReturnType<typeof useQueryClient>): void {
	void client.invalidateQueries({ queryKey: ["help"] });
	void client.invalidateQueries({ queryKey: ["memory", "gaps"] });
	void client.invalidateQueries({ queryKey: ["onboarding", "state"] });
	// Uma pergunta de missão RETOMA a missão ao ser respondida (`pendingWhisper`):
	// a linha do run muda de `waiting_human` para `running` do lado do servidor.
	void client.invalidateQueries({ queryKey: ["missions"] });
}

export function useAnswerHelp() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { id: string; answer: string; answerFiles?: string[] }) =>
			request<HumanRequest>(`/v3/help/${input.id}/answer`, {
				method: "POST",
				body: { answer: input.answer, answerFiles: input.answerFiles ?? [] },
			}),
		onSuccess: () => invalidateEverythingAnAnswerMoves(client),
	});
}

export function useDismissHelp() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => request(`/v3/help/${id}/dismiss`, { method: "POST" }),
		// Descartar fecha a pergunta tanto quanto responder — o passo da home e o
		// portão do site contam as ABERTAS, e uma descartada deixou de o ser.
		onSuccess: () => invalidateEverythingAnAnswerMoves(client),
	});
}
